import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API from './api';
import GuardedCheckoutConsent, { useGuardedCheckout } from './components/GuardedCheckout';
import { getCombatProOwned, getTierSpec, TIER_ORDER, COMBAT_TIERS } from './combatProTiers';
import { CombatProShell, TierCard, cps } from './CombatProShared';

export default function CombatProMembershipPage() {
  // Shared consent controller. Inert unless the SERVER refuses the checkout pending an
  // immediate-supply acknowledgement; then it renders the server's own wording and retries.
  const consentCheckout = useGuardedCheckout();
  const navigate = useNavigate();
  const { user } = useAuth();
  const owned = getCombatProOwned(user?.entitlements?.combat_system_pro);
  const [tier, setTier] = useState('none');
  const [account, setAccount] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    API.get('/v1/combat-commerce/account').then((res) => {
      setAccount(res.data);
      setTier(res.data.combat_cloud?.status === 'active' ? res.data.combat_cloud.tier : 'none');
    });
  }, []);

  const spec = getTierSpec(tier);
  const cloud = tier !== 'none';
  const tierDef = COMBAT_TIERS[tier] || COMBAT_TIERS.none;

  // Standalone generators are a separate VISINVOKE product family under Game Systems and are NOT
  // presented on any Combat PRO surface. Combat PRO membership neither grants nor summarises them.

  const yn = (b) => (b ? 'Yes' : 'No');

  const entLeft = [
    ['Current Plan', tierDef.name],
    ['Prepared Encounters', account?.prepared_encounter_limit ?? spec.savedEncounters],
    ['Cloud Saves', yn(spec.cloudSaves)],
    ['Cross Device', yn(spec.crossDevice)],
  ];
  const entRight = [
    ['Monster Cloud', yn(spec.monsterCloud)],
    ['Spell Cloud', yn(spec.spellCloud)],
    ['Cloud Status', cloud ? 'Active' : 'Not subscribed'],
    ['Billing State', account?.combat_cloud?.cancel_at_period_end ? 'Cancels at period end' : (account?.combat_cloud?.status || '\u2014')],
    ['Period End', account?.combat_cloud?.current_period_end ? new Date(account.combat_cloud.current_period_end).toLocaleDateString() : '\u2014'],
  ];

  async function chooseCloud(tierKey) {
    setBusy(true);
    try {
      const res = await consentCheckout.start(() => API.post('/v1/combat-commerce/cloud/checkout', { tier: tierKey }), { purchaseType: 'combat_pro_cloud', intendedSelector: tierKey });
      // null means the shared controller is showing the acknowledgement instead of completing.
      if (res && res.data.checkout_url) window.location.assign(res.data.checkout_url);
      else setTier(tierKey);
    } finally { setBusy(false); }
  }

  async function manageBilling() {
    setBusy(true);
    try {
      const res = await API.post('/api/payments/create-billing-portal-session', { return_url: window.location.href });
      window.location.assign(res.data.url || res.data.portal_url);
    } finally { setBusy(false); }
  }
  return (
    <CombatProShell title="Membership" subtitle="Your Combat PRO ownership, current tier, and account activity." navigate={navigate}>
      <GuardedCheckoutConsent checkout={consentCheckout} />

      {/* TOP — Expanded entitlement card */}
      <div style={{ ...cps.currentPlanCard, borderColor: tierDef.color, boxShadow: '0 0 0 1px ' + tierDef.color + '66, 0 10px 34px rgba(0,0,0,0.4)' }}>
        <div style={cps.cpHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <span style={cps.cpKicker}>Current Entitlements</span>
            <span style={{ ...cps.cpBadge, background: tierDef.color + '26', color: tierDef.color, border: '1px solid ' + tierDef.color + '99' }}>{tierDef.name}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: owned ? '#4ade80' : 'rgba(255,255,255,0.6)' }}>{owned ? 'Combat PRO Owned' : 'Combat PRO Not Owned'}</span>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {account?.combat_cloud && <button disabled={busy} style={{ ...cps.btn, ...cps.btnGhost, width: 'auto', padding: '10px 18px' }} onClick={manageBilling}>Manage / cancel</button>}
          </div>
        </div>
        <div style={cps.cpDivider} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 30px' }}>
          <div style={cps.specWrap}>
            {entLeft.map(([label, val], i) => (
              <div key={i} style={cps.specRow}>
                <span style={cps.specLabel}>{label}</span>
                <span style={cps.specVal}>{val}</span>
              </div>
            ))}
          </div>
          <div style={cps.specWrap}>
            {entRight.map(([label, val], i) => (
              <div key={i} style={cps.specRow}>
                <span style={cps.specLabel}>{label}</span>
                <span style={cps.specVal}>{val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* MIDDLE — Membership tier cards */}
      <div style={cps.sectionTitle}>Membership Tiers</div>
      <div style={cps.tierGrid}>
        {TIER_ORDER.map((t) => (
          <TierCard key={t} tierKey={t} currentTier={tier} onChoose={chooseCloud} busy={busy} />
        ))}
      </div>

      {/* Membership Notes (4 cards) */}
      <div style={{ paddingTop: 24 }}>
        <div style={cps.sectionTitle}>Membership Notes</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {[
            { head: 'Combat PRO Ownership', lines: ['Combat PRO ownership is permanent.', 'Membership is optional.'] },
            { head: 'Cloud Features', lines: ['Membership enables cloud storage and synchronization.', 'Combat PRO ownership remains valid and the base product stays usable without a Cloud plan.'] },
            { head: 'Upgrades', lines: ['Membership can be upgraded, downgraded, or cancelled at any time.', 'Combat PRO ownership is never lost.'] },
          ].map((c, i) => (
            <div key={i} style={{ ...cps.card }}>
              <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: 10 }}>{c.head}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7, fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>
                {c.lines.map((line, j) => (<div key={j}>{line}</div>))}
              </div>
              {c.action && <button style={{ ...cps.btn, marginTop: 12, width: 'auto', padding: '9px 18px' }} onClick={c.action.onClick}>{c.action.label}</button>}
            </div>
          ))}
        </div>
      </div>
    </CombatProShell>
  );
}
